/**
 * PrivacyPage.js
 * DataQuad Privacy Policy — public page
 */

import React, { useEffect } from 'react';
import { useTheme } from '../utils/ThemeSwitcher';
import { ArrowLeft, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PrivacyPage = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&display=swap';
    link.rel = 'stylesheet';
    if (!document.querySelector('link[href*="Outfit"]')) document.head.appendChild(link);
    window.scrollTo(0, 0);
    document.title = 'Privacy Policy — Noble Syntax';
  }, []);

  const colors = {
    bg: isDarkMode ? '#08090B' : '#FFFFFF',
    cardBg: isDarkMode ? 'rgba(255,255,255,0.03)' : '#FFFFFF',
    text: isDarkMode ? '#E5E7EB' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    heading: isDarkMode ? '#F9FAFB' : '#111827',
    border: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    accent: '#2B8AFF',
  };

  const sectionStyle = { marginBottom: '32px' };
  const h2Style = { fontSize: '20px', fontWeight: '600', color: colors.heading, marginBottom: '12px', marginTop: 0 };
  const h3Style = { fontSize: '16px', fontWeight: '600', color: colors.heading, marginBottom: '8px', marginTop: '20px' };
  const pStyle = { fontSize: '15px', lineHeight: '1.75', color: colors.text, margin: '0 0 12px 0' };
  const liStyle = { fontSize: '15px', lineHeight: '1.75', color: colors.text, marginBottom: '6px' };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: colors.bg,
      fontFamily: "'Outfit', -apple-system, BlinkMacSystemFont, sans-serif",
    }}>
      {/* Header */}
      <div style={{
        borderBottom: `1px solid ${colors.border}`,
        backgroundColor: isDarkMode ? 'rgba(8,9,11,0.92)' : 'rgba(255,255,255,0.92)',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backdropFilter: 'blur(12px)',
      }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '16px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <button
            onClick={() => navigate('/')}
            style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              background: 'none', border: 'none', cursor: 'pointer',
              color: colors.accent, fontSize: '14px', fontWeight: '500',
              fontFamily: 'inherit',
            }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: colors.textSecondary, fontSize: '13px' }}>
            <FileText size={14} />
            <span>Legal</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '800px', margin: '0 auto', padding: '48px 24px 80px' }}>
        <h1 style={{ fontSize: '32px', fontWeight: '700', color: colors.heading, marginBottom: '8px' }}>Privacy Policy</h1>
        <p style={{ fontSize: '14px', color: colors.textSecondary, marginBottom: '40px' }}>Last updated: January 27, 2026</p>

        <div style={{
          backgroundColor: colors.cardBg,
          borderRadius: '16px',
          border: `1px solid ${colors.border}`,
          padding: '40px',
        }}>
          <div style={sectionStyle}>
            <p style={pStyle}>
              Noble Syntax Limited ("we", "us", "our") operates DataQuad, an AI-powered data quality assessment platform.
              This Privacy Policy explains how we collect, use, store, and protect your information when you use our Service.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>1. Information We Collect</h2>

            <h3 style={h3Style}>1.1 Account Information</h3>
            <p style={pStyle}>When you register, we collect:</p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>Full name</li>
              <li style={liStyle}>Email address</li>
              <li style={liStyle}>Organization name (optional)</li>
              <li style={liStyle}>Profile picture (if signing in via Google)</li>
            </ul>

            <h3 style={h3Style}>1.2 Survey Data</h3>
            <p style={pStyle}>
              When you use DataQuad, you upload survey data for quality analysis. This may include datasets from
              KoBoToolbox, SurveyCTO, ODK, and similar platforms. We process this data solely to provide the Service.
              We do not access, read, or analyse the content of your survey data for any purpose other than
              delivering the features you request.
            </p>

            <h3 style={h3Style}>1.3 Usage Data</h3>
            <p style={pStyle}>We automatically collect:</p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>Browser type and version</li>
              <li style={liStyle}>Pages visited and features used</li>
              <li style={liStyle}>Date and time of access</li>
              <li style={liStyle}>Device and operating system information</li>
            </ul>

            <h3 style={h3Style}>1.4 Payment Information</h3>
            <p style={pStyle}>
              Payment processing is handled entirely by Paddle, our Merchant of Record.
              We do not store credit card numbers, bank account details, or other financial information
              on our servers. Paddle collects and processes payment data in accordance with PCI DSS standards.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>2. How We Use Your Information</h2>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>To provide, maintain, and improve the Service</li>
              <li style={liStyle}>To process your subscription and manage your account</li>
              <li style={liStyle}>To communicate with you about your account, updates, and support requests</li>
              <li style={liStyle}>To detect and prevent fraud, abuse, or security threats</li>
              <li style={liStyle}>To comply with legal obligations</li>
            </ul>
            <p style={pStyle}>
              We do <strong>not</strong> sell, rent, or share your personal information with third parties
              for marketing purposes. We do <strong>not</strong> use your uploaded survey data to train AI models
              or for any purpose beyond providing the Service to you.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>3. Data Storage and Security</h2>
            <p style={pStyle}>
              Your data is hosted on secure cloud infrastructure (DigitalOcean) with industry-standard protections including:
            </p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}>Encryption in transit (TLS/SSL) and at rest</li>
              <li style={liStyle}>Regular security updates and vulnerability scanning</li>
              <li style={liStyle}>Access controls limiting data access to authorized systems</li>
              <li style={liStyle}>Regular automated backups</li>
            </ul>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>4. Third-Party Services</h2>
            <p style={pStyle}>We use the following third-party services that may process your data:</p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}><strong>Paddle</strong> — Payment processing and subscription management</li>
              <li style={liStyle}><strong>Google OAuth</strong> — Authentication (if you choose to sign in with Google)</li>
              <li style={liStyle}><strong>DigitalOcean</strong> — Cloud hosting and data storage</li>
              <li style={liStyle}><strong>Vercel</strong> — Frontend application hosting</li>
            </ul>
            <p style={pStyle}>
              Each third-party provider has their own privacy policy governing their handling of data.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>5. Data Retention</h2>
            <p style={pStyle}>
              Your account data is retained for as long as you maintain an active account. Uploaded survey data
              is retained while your account is active. Upon account deletion or subscription termination,
              we retain your data for 90 days to allow for recovery, after which it is permanently deleted.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>6. Your Rights</h2>
            <p style={pStyle}>You have the right to:</p>
            <ul style={{ paddingLeft: '20px', margin: '12px 0' }}>
              <li style={liStyle}><strong>Access</strong> — Request a copy of the personal data we hold about you</li>
              <li style={liStyle}><strong>Correction</strong> — Request correction of inaccurate personal data</li>
              <li style={liStyle}><strong>Deletion</strong> — Request deletion of your personal data and uploaded survey data</li>
              <li style={liStyle}><strong>Export</strong> — Download your data at any time in standard formats (CSV, Excel, Stata)</li>
              <li style={liStyle}><strong>Restrict processing</strong> — Request that we limit how we use your data</li>
            </ul>
            <p style={pStyle}>
              To exercise any of these rights, contact us at privacy@noblesyntax.com.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>7. Cookies</h2>
            <p style={pStyle}>
              DataQuad uses essential cookies and local storage to maintain your session, remember your preferences
              (such as dark/light mode), and keep you logged in. We do not use advertising or tracking cookies.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>8. Children's Privacy</h2>
            <p style={pStyle}>
              DataQuad is not intended for use by individuals under the age of 18. We do not knowingly collect
              personal information from children. If we become aware that we have collected data from a minor,
              we will take steps to delete it promptly.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>9. International Data Transfers</h2>
            <p style={pStyle}>
              Your data may be processed on servers located outside of Zambia. By using the Service, you consent
              to the transfer of your information to countries that may have different data protection laws.
              We ensure appropriate safeguards are in place for any such transfers.
            </p>
          </div>

          <div style={sectionStyle}>
            <h2 style={h2Style}>10. Changes to This Policy</h2>
            <p style={pStyle}>
              We may update this Privacy Policy from time to time. Material changes will be communicated via
              email or a notice within the Service. The "Last updated" date at the top of this page indicates
              when the policy was last revised.
            </p>
          </div>

          <div style={{ ...sectionStyle, marginBottom: 0 }}>
            <h2 style={h2Style}>11. Contact Us</h2>
            <p style={pStyle}>For questions about this Privacy Policy, please contact us:</p>
            <p style={{ ...pStyle, marginBottom: 0 }}>
              <strong>Noble Syntax Limited</strong><br />
              Email: privacy@noblesyntax.com<br />
              Website: dataquadapp.com<br />
              Location: Lusaka, Zambia
            </p>
          </div>
        </div>
      </div>

      <style>{`
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'}; }
      `}</style>
    </div>
  );
};

export default PrivacyPage;